import { Component, ViewEncapsulation } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import {
  SetSelectedNodesAction,
  SetUserProfileAction,
} from '@alfresco/aca-shared/store';
import { LogoutDirective } from '@alfresco/adf-core';
import {
  AppStore,
  SetCurrentSiteAction,
  SetSiteChildrenAction,
  SetSitesAction,
  currentSiteSelector,
} from '../../../../store';
import { Observable, Subject } from 'rxjs';
import { filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ProfileState } from '@alfresco/adf-extensions';
import { ContentService } from '../../../shared/services/content.service';
import { WkAuthenticationService } from '../../../../services';
import { CONSTANTS } from '../../../../model/custom-adf-model';

@Component({
  standalone: true,
  imports: [TranslateModule, MatIconModule, MatMenuModule, LogoutDirective],
  selector: 'wk-logout-menu',
  templateUrl: './wk-logout.component.html',
  styleUrls: ['./wk-logout.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class WkLogoutMenuComponent {
  private readonly _onDestroy = new Subject<void>();
  public profile$: Observable<ProfileState>;
  public currentUserId: string;
  public currentSite: string;
  constructor(
    private readonly store: Store<AppStore>,
    private readonly router: Router,
    private readonly auth: WkAuthenticationService,
    private readonly wkContentService: ContentService
  ) {}

  onLogoutEvent() {
    this.store.dispatch(new SetSelectedNodesAction([]));
    this.store
      .select(currentSiteSelector)
      .pipe(
        takeUntil(this._onDestroy),
        tap((currentSite) => {
          if (currentSite !== '' && currentSite !== 'no-site') {
            this.currentSite = currentSite;
          } else {
            this.logout();
          }
        }),
        filter(
          (currentSite) => currentSite !== '' && currentSite !== 'no-site'
        ),
        switchMap(() => this.profile$.pipe(take(1))),
        tap((profileState) => (this.currentUserId = profileState.id)),
        switchMap(() =>
          this.wkContentService.setLastloginSite(
            this.currentUserId,
            this.currentSite
          )
        )
      )
      .subscribe({
        next: () => this.logout(),
        error: () => this.logout(),
      });
  }

  logout() {
    this._onDestroy.next();
    this._onDestroy.complete();
    this.auth.logout().subscribe({
      next: () => this.redirectToLogout(),
      error: () => this.redirectToLogout(),
    });
  }

  redirectToLogout() {
    this.router.navigate(['logout']).then(() => {
      this.removeLocalStorageData();
      this.wkContentService.closeBroadcastChannel();
      this.store.dispatch(new SetSitesAction([]));
      this.store.dispatch(new SetSiteChildrenAction([]));
      this.store.dispatch(new SetCurrentSiteAction(''));
      (window as any).Alfresco.constants.USERNAME = '';
    });
  }

  removeLocalStorageData() {
    if (localStorage.getItem(CONSTANTS.DOCUMENT_LIBRARY)) {
      localStorage.removeItem(CONSTANTS.DOCUMENT_LIBRARY);
    }
    if (localStorage.getItem(CONSTANTS.EMAIL_TEMPLATES)) {
      localStorage.removeItem(CONSTANTS.EMAIL_TEMPLATES);
    }
    if (localStorage.getItem(CONSTANTS.CONTENT_TEMPLATES)) {
      localStorage.removeItem(CONSTANTS.CONTENT_TEMPLATES);
    }
    if (localStorage.getItem(CONSTANTS.CONTRACT_TEMPLATES)) {
      localStorage.removeItem(CONSTANTS.CONTRACT_TEMPLATES);
    }
    if (localStorage.getItem(CONSTANTS.CURRENT_USER)) {
      localStorage.removeItem(CONSTANTS.CURRENT_USER);
    }
    if (localStorage.getItem(CONSTANTS.CURRENT_SITE)) {
      localStorage.removeItem(CONSTANTS.CURRENT_SITE);
    }
    if (localStorage.getItem(CONSTANTS.CURRENT_FOLDER)) {
      localStorage.removeItem(CONSTANTS.CURRENT_FOLDER);
    }
    // deletion of user configuration for All Documents Dashboard
    if (localStorage.getItem(CONSTANTS.ADD_COLUMNS_USER_CONFIGURATION)) {
      localStorage.removeItem(CONSTANTS.ADD_COLUMNS_USER_CONFIGURATION);
    }
    this.store.dispatch(
      new SetUserProfileAction({
        id: '',
        isAdmin: false,
        firstName: '',
        lastName: '',
        person: { id: '', email: '', firstName: '', enabled: false },
        groups: [],
      })
    );
  }
}
